import { motion, AnimatePresence } from 'framer-motion'
import { X, Heart, MessageSquare, Smile, Frown, Meh, Calendar, Sparkles, TrendingUp } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface CheckInDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

const CheckInDetailsModal = ({ isOpen, onClose }: CheckInDetailsModalProps) => {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              <div className="sticky top-0 z-10 bg-gradient-to-r from-pink-600/20 to-rose-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                      <Heart className="h-8 w-8 text-pink-400" />
                      {t('checkIn.modal.title')}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">{t('checkIn.modal.subtitle')}</p>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-pink-500/10 to-rose-500/10 rounded-2xl p-6 border border-pink-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-pink-500/20 rounded-xl">
                        <Sparkles className="h-6 w-6 text-pink-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('checkIn.modal.whatIs')}</h3>
                        <p className="text-gray-300 leading-relaxed">{t('checkIn.modal.intro')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('checkIn.modal.howItWorks')}</h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl p-6 border border-pink-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                          <Calendar className="h-6 w-6 text-pink-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('checkIn.modal.step1.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('checkIn.modal.step1.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl p-6 border border-rose-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-rose-500/20 rounded-xl flex items-center justify-center">
                          <Heart className="h-6 w-6 text-rose-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('checkIn.modal.step2.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('checkIn.modal.step2.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/5 rounded-2xl p-6 border border-purple-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                          <MessageSquare className="h-6 w-6 text-purple-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('checkIn.modal.step3.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('checkIn.modal.step3.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/5 rounded-2xl p-6 border border-blue-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                          <TrendingUp className="h-6 w-6 text-blue-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('checkIn.modal.step4.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('checkIn.modal.step4.description')}</p>
                    </motion.div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('checkIn.modal.moodOptions')}</h3>

                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      { icon: Smile, mood: t('checkIn.modal.mood1.title'), desc: t('checkIn.modal.mood1.description'), color: 'green' },
                      { icon: Meh, mood: t('checkIn.modal.mood2.title'), desc: t('checkIn.modal.mood2.description'), color: 'yellow' },
                      { icon: Frown, mood: t('checkIn.modal.mood3.title'), desc: t('checkIn.modal.mood3.description'), color: 'red' },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className={`bg-gradient-to-br from-${item.color}-500/20 to-${item.color}-600/20 rounded-2xl p-6 border border-${item.color}-500/30 text-center`}
                      >
                        <div className={`w-16 h-16 bg-${item.color}-500/30 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <item.icon className={`h-8 w-8 text-${item.color}-400`} />
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">{item.mood}</h4>
                        <p className="text-sm text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('checkIn.modal.questions')}</h3>

                  <div className="space-y-4">
                    {[
                      t('checkIn.modal.question1'),
                      t('checkIn.modal.question2'),
                      t('checkIn.modal.question3'),
                      t('checkIn.modal.question4'),
                      t('checkIn.modal.question5'),
                    ].map((question, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-gradient-to-r from-white/5 to-white/10 rounded-2xl p-5 border border-white/10"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-pink-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-pink-300 text-xs font-bold">{i + 1}</span>
                          </div>
                          <p className="text-gray-200">{question}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('checkIn.modal.benefits')}</h3>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      { icon: Heart, title: t('checkIn.modal.benefit1.title'), desc: t('checkIn.modal.benefit1.description') },
                      { icon: TrendingUp, title: t('checkIn.modal.benefit2.title'), desc: t('checkIn.modal.benefit2.description') },
                      { icon: Sparkles, title: t('checkIn.modal.benefit3.title'), desc: t('checkIn.modal.benefit3.description') },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-2xl p-6 border border-pink-500/30"
                      >
                        <div className="w-12 h-12 bg-pink-500/30 rounded-xl flex items-center justify-center mb-4">
                          <item.icon className="h-6 w-6 text-pink-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-pink-600/20 to-rose-600/20 rounded-2xl p-8 border border-pink-500/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-3">{t('checkIn.modal.cta')}</h3>
                  <p className="text-gray-300 mb-6">{t('checkIn.modal.ctaDescription')}</p>
                  <button
                    onClick={onClose}
                    className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold rounded-xl transition-all"
                  >
                    {t('checkIn.modal.gotIt')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CheckInDetailsModal
